import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { ComboWeatherSnapshot } from '../db';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// --- types ---

export interface FertilizerProduct {
  _id: string;
  productName: string;
  consumableType: 'fertilizer';
  analysis: { n: number; p: number; k: number };
  applicationMethods: ('soil' | 'foliar')[];
  stockUnit?: string;
  needsReview?: boolean;
  leadTimeDays?: number;
  updatedAt?: string;
}

export interface FertilizerInput {
  productName: string;
  analysis: { n: number; p: number; k: number };
  applicationMethods: ('soil' | 'foliar')[];
  stockUnit?: string;
  leadTimeDays?: number;
}

export interface ProgrammeProductLine {
  productId: string;
  productName?: string;
  unit: string;
  doseBasis?: 'perTree' | 'per100L';
  value: number;
}

export interface ProgrammeGroup {
  groupId: string;
  label?: string;
  orchardIds: string[];
  products: ProgrammeProductLine[];
}

export interface SoilRound {
  roundId: string;
  type: 'soil' | 'foliar';
  name: string;
  month?: number;
  plannedStart?: string;
  waterRatePerHa?: number;
  groups: ProgrammeGroup[];
}

export interface NutritionProgramme {
  _id: string;
  season: string;
  source?: { vendor?: string; contact?: string };
  status: 'received' | 'reviewed' | 'active' | 'superseded';
  rounds: SoilRound[];
  youngTreeProgramme?: { name?: string; products: ProgrammeProductLine[] } | null;
  createdAt: string;
  deletedAt?: string | null;
}

export interface NutritionAuditOrchard {
  orchardId: string;
  orchardNo: string;
  cultivar: string;
  ha: number;
  n: number | null; p: number | null; k: number | null;
  nPerHa: number | null; pPerHa: number | null; kPerHa: number | null;
  status: 'applied' | 'partial' | 'not_applied';
  unlinkedCount: number;
}
export interface NutritionAuditSeason {
  n: number; p: number; k: number;
  nPerHa: number | null; pPerHa: number | null; kPerHa: number | null;
  totalHa: number;
}
export type NutritionAuditSource = 'soil' | 'foliar' | 'all';
export interface NutritionAudit {
  selectedFrom: string;
  selectedTo: string;
  source: NutritionAuditSource;
  months: string[];
  orchards: NutritionAuditOrchard[];
  season: NutritionAuditSeason;
  needsLinking: number;
}
export interface NutritionAuditFilters { from?: string; to?: string; source?: NutritionAuditSource; }

export interface FoliarBoardRoundSessions {
  total: number; draft: number; planned: number; inProgress: number;
  completed: number; incomplete: number; signedOff: number;
  awaitingSignoff: number;
  oldestAwaitingCompletedAt: string | null;
  /**
   * Raw ISO completion timestamps for every `completed` or `signed_off` session
   * in this round — quoting Task 2's Interfaces block. NOT pre-bucketed by
   * month: the server would have to choose a timezone, and `currentMonthKey` is
   * LOCAL. `summariseFoliarBoard` buckets these client-side (Task 5).
   */
  completedAts: string[];
}
export interface FoliarBoardRound {
  roundId: string; name: string; month: number | null; plannedStart: string | null;
  waterRatePerHa: number | null; orchardCount: number; productNames: string[];
  sessions: FoliarBoardRoundSessions;
}
export interface FoliarBoard {
  programme: { _id: string; season: string; status: string; source?: { vendor?: string; contact?: string } };
  rounds: FoliarBoardRound[];
}

export interface FoliarRosterCombo {
  comboId: string;
  operatorId: string | null;
  operatorName: string;
  machineId: string | null;
  machineName: string;
  status: string;
}

export interface FoliarRosterMixLine {
  productId: string;
  productName: string;
  dosePerTank: number;
  doseUnit: string;
  n: number;
  p: number;
  k: number;
}

/**
 * Dose-B2 fix: machine-independent — `totalQty / (totalWaterL / 100)`, the
 * concentration in the tank regardless of dose basis (per100L or perTree)
 * and regardless of whether a machine has been assigned. `dosePer100L` is
 * null (never 0/NaN/Infinity) when totalWaterL is 0.
 */
export interface FoliarRosterMixPer100LLine {
  productId: string;
  productName: string;
  doseUnit: string;
  dosePer100L: number | null;
}

export interface FoliarRosterRow {
  sessionId: string;
  orchardNo: string;
  cultivar: string;
  /** #460: null when the orchard record was not found — distinct from a genuine 0 ha. */
  areaHa: number | null;
  /** #460: null when the orchard record was not found — distinct from a genuine 0 trees. */
  trees: number | null;
  /** #460: null when the session carries no plan yet — distinct from a genuine 0 L. */
  waterL: number | null;
  /**
   * #460/D5: null when no machine is assigned to this session's combos, OR
   * when more than one distinct machine is — each combo's plannedTankCount
   * means "tanks if THIS machine did the whole orchard", so two combos on
   * two machines have two different correct answers and there is no single
   * session-level figure to report (see foliarRosterService.js's own
   * comment). Distinct from a genuine 0.
   */
  tanksPlanned: number | null;
  /** null means nothing has been captured yet — distinct from a captured 0. */
  tanksApplied: number | null;
  status: string;
  completedAt: string | null;
  signedOffAt: string | null;
  combos: FoliarRosterCombo[];
  /** Always an array — [] when the session has no calculated mix yet. */
  perTankMix: FoliarRosterMixLine[];
  /** Always an array — [] when the session has no calculated mix yet. */
  mixPer100L: FoliarRosterMixPer100LLine[];
  /**
   * Dose-B2 fix: the tank volume, in litres, of the machine whose per-tank
   * figures this row carries — its REFERENCE combo, the first one with a
   * resolvable machine (foliarRosterService.js). Null until a machine is
   * assigned.
   *
   * THE CANONICAL EXPLANATION of the per-tank null guard; other comments in
   * this feature defer here.
   *
   * The UI must not show a per-tank dose or a tank count when this is null.
   * A per-tank figure is a fact about a MACHINE, so it is computed once — by
   * `assignCombo`, from that combo's own machine — and stored on the COMBO
   * (#461). Conversion knows no machine and writes none, so on an unassigned
   * row `perTankMix` is simply `[]` and `tanksPlanned` is null: there is
   * nothing to fall back to, and nothing may be invented in its place.
   *
   * (Conversion USED TO write per-tank figures, against a nominal 1000 L tank
   * that exists on no farm — understating the dose ~3x on the 3000 L rig this
   * farm actually runs. #461 stopped that write and its boot migration strips
   * the stale keys from every stored foliar session, so those figures no
   * longer exist anywhere to be shown by accident.)
   *
   * When it is NON-null it must be rendered beside the dose: a session can
   * carry combos on several machines, and this row's figures describe only
   * the reference combo's one, so "per tank" is unreadable without its size.
   */
  tankVolumeL: number | null;
}

export interface FoliarRoster {
  round: {
    roundId: string;
    name: string;
    month: number | null;
    plannedStart: string | null;
    waterRatePerHa: number | null;
    productNames: string[];
    /** #475: cross-family dose-unit conflicts. Always an array. */
    unitConflicts: Array<{
      productId: string; productName: string; unit: string;
      stockUnit: string; reason: string; message: string;
    }>;
  };
  /** The programme's season string, from the server. */
  programmeName: string;
  rows: FoliarRosterRow[];
}

export interface AssignBulkResult {
  results: Array<{ sessionId: string; ok: boolean; error?: string }>;
  okCount: number;
  failCount: number;
}

export interface FoliarAppliedProduct {
  productId: string;
  productName: string;
  quantity: number;
  quantityUnit: string | null;
  /**
   * The WHOLE SESSION's planned quantity, not this combo's share — deliberately
   * (spec §6.2: `recomputeParentStatus` copies this field onto the parent
   * rather than summing it). Null when the completion predates the snapshot.
   *
   * NOTHING ON THIS PAGE READS IT (R15). Comparing a combo's portion against
   * it reports a large negative variance for work done exactly to plan, and
   * summing it across combos multiplies the plan by the combo count. The
   * planned-vs-applied comparison is session-level and reads
   * `session.productTotals`; see foliarSessionDetail.sessionVariance.
   */
  plannedQuantity: number | null;
}

export interface FoliarDetailCombo {
  comboId: string;
  operatorId: string | null;
  operatorName: string;
  machineId: string | null;
  machineName: string;
  machineTankVolumeL: number | null;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  completedAt: string | null;
  captureMode: 'live' | 'back_capture';
  /**
   * (#461/D5) This combo's own planned tank count, derived from ITS OWN
   * machine — not a session-level scalar. Two combos on two machines have two
   * different correct answers, so there is no single session-level figure;
   * each is shown against the machine it belongs to. Null until this combo
   * has both a machine assignment and a plan to derive from.
   */
  plannedTankCount: number | null;
  /** The un-ceilinged fraction beside plannedTankCount (e.g. 0.55 tanks) — shown
   *  only when it differs from the ceiling. Null under the same conditions as
   *  plannedTankCount. */
  plannedTanksExact: number | null;
  /** null means nothing captured — distinct from a captured 0. */
  tanksApplied: number | null;
  weatherStart: ComboWeatherSnapshot | null;
  weatherEnd: ComboWeatherSnapshot | null;
  productsApplied: FoliarAppliedProduct[];
}

export interface FoliarSessionDetail {
  session: {
    sessionId: string; programmeId: string; roundId: string; roundName: string;
    /** The programme's season string, from the server. */
    programmeName: string;
    name: string; orchardNo: string; status: string;
    captureMode: 'live' | 'back_capture';
    date: string | null; startedAt: string | null;
    completedAt: string | null; signedOffAt: string | null;
    // (#461/D5) `tanksPlanned` and `tanksExact` no longer exist on the wire —
    // a single session-level scalar cannot describe two combos on different
    // machines. See combos[].plannedTankCount / combos[].plannedTanksExact.
    /** #510.2: null, never 0 — an unplanned session vs. a genuine zero. */
    waterL: number | null;
    productTotals: Array<{
      productId: string; productName: string; totalQty: number; unit: string;
      n: number; p: number; k: number;
    }>;
  };
  /** #460: null, never 0/'' — unknown orchard vs zero hectares. */
  orchard: { cultivar: string | null; areaHa: number | null; trees: number | null };
  /** #461: null until a real machine is assigned. */
  tankVolumeL: number | null;
  combos: FoliarDetailCombo[];
}

export interface FoliarAwaitingRow {
  sessionId: string; programmeId: string; season: string | null;
  roundId: string; roundName: string; orchardNo: string;
  operatorNames: string[];
  tanksApplied: number | null;
  completedAt: string | null;
}

export interface FoliarSignOffBulkResult {
  results: Array<{ sessionId: string; ok: boolean; error?: string }>;
  okCount: number;
  failCount: number;
}

export interface FertilizerStockUsageRow {
  productId: string;
  productName: string;
  unit: 'L' | 'kg' | 'g' | 'ml' | null;
  draft: number;
  planned: number;
  /**
   * Null when the product's on-hand figure is in a unit-conflict state
   * (Slice 4): a cross-family unit conflict means the backend can't safely
   * report a single number. Never do arithmetic on this without a null
   * check; render the `inventory.unitConflict.badge` review pill instead.
   */
  stock: number | null;
  cPlanned: number;
  cActual: number;
  /**
   * Present (with stock: null) when the on-hand figure is unit-conflicted.
   * Derived stockStatus (see fertilizerStockUsageMath) also reports 'conflict'
   * whenever stock is null, so this is a reason code only, not a status flag.
   */
  conflictReason?: string;
}

export interface NutritionSummary {
  month: string;
  appliedThisMonth: { orchards: number; applications: number; n: number; p: number; k: number; needsLinking: number };
  foliarWindowsOpen: number;
  signoffsOutstanding: { count: number; rows: Array<{ sessionId: string; orchardNo: string; roundName: string; season: string | null }> };
  rounds: Array<{ programmeId: string; season: string; rounds: Array<{ roundId: string; name: string; month?: number; total: number; generated: number; completed: number; status: 'not_generated' | 'generated' | 'in_progress' | 'completed' }> }>;
  /**
   * Soil coverage by month, per active SoilProgram (#443). Soil has no rounds —
   * a SoilProgram is entries[] of orchard x month x product, so it is
   * summarised by month, never forced into the foliar rounds shape.
   */
  soil: {
    coverage: Array<{ programId: string; season: string; months: Array<{ month: string; total: number; complete: number; signedOff: number }> }>;
    outstanding: number;
  };
  generatedAt: string;
  fertilizerStockUsage?: FertilizerStockUsageRow[];
  inventory?: {
    onHandValue: number; dwellBreaches: number; dwellLow: number;
    dwellWatch: Array<{ productId: string; productName: string; onHand: number; unit: string | null; dwell: { days: number | null; limit: number; status: string } | null }>;
    stockCover: number | null; orderBy: string | null;
    /**
     * Demand records that actually need ordering — the same predicate the
     * order-to-need page lists by. NOT the size of the demand schedule
     * (`OrderToNeed.roundCount`), which counts covered records too and made the
     * dashboard alert contradict the page it links to.
     */
    orderNeedCount: number;
    orderRows: Array<{ productId: string; productName?: string; unit: string; demand: number; onHand: number; onOrder: number; orderQty: number; orderBy: string | null }>;
  };
  audit?: {
    orchards: Array<{ orchardId: string; orchardNo: string; ha: number; n: number | null; p: number | null; k: number | null; status: string }>;
    season: { n: number; p: number; k: number; nPerHa: number | null; pPerHa: number | null; kPerHa: number | null; totalHa: number };
    needsLinking: number;
  };
}

const base = '/api/v1/nutrition';

export const nutritionService = {
  async listProgrammes(): Promise<NutritionProgramme[]> {
    try {
      const res = await axios.get<NutritionProgramme[]>(`${base}/programmes`, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getProgramme(id: string): Promise<NutritionProgramme> {
    try {
      const res = await axios.get<NutritionProgramme>(`${base}/programmes/${id}`, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async createProgramme(body: Partial<NutritionProgramme>): Promise<NutritionProgramme> {
    try {
      const res = await axios.post<NutritionProgramme>(`${base}/programmes`, body, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async cloneProgramme(id: string): Promise<NutritionProgramme> {
    try {
      const res = await axios.post<NutritionProgramme>(
        `${base}/programmes/${id}/clone`,
        {},
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async updateProgramme(id: string, body: Partial<NutritionProgramme>): Promise<NutritionProgramme> {
    try {
      const res = await axios.put<NutritionProgramme>(`${base}/programmes/${id}`, body, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Replace ONE round of a programme. Round-SCOPED on purpose: the
   * whole-programme PUT rewrites doc.rounds outright, so a round editor using
   * it would re-serialise every sibling through this client's shape.
   * The body is a complete round — null clears an optional field.
   */
  async updateProgrammeRound(
    programmeId: string,
    roundId: string,
    body: SoilRound,
  ): Promise<NutritionProgramme> {
    try {
      const res = await axios.put<NutritionProgramme>(
        `${base}/programmes/${programmeId}/rounds/${roundId}`,
        body,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deleteProgramme(id: string): Promise<void> {
    try {
      await axios.delete(`${base}/programmes/${id}`, { headers: authHeaders() });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async listFertilizers(): Promise<FertilizerProduct[]> {
    try {
      const res = await axios.get<FertilizerProduct[]>('/api/v1/consumables/fertilizers', {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async createFertilizer(body: FertilizerInput): Promise<FertilizerProduct> {
    try {
      const res = await axios.post<FertilizerProduct>('/api/v1/consumables/fertilizers', body, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async updateFertilizer(id: string, body: Partial<FertilizerInput>): Promise<FertilizerProduct> {
    try {
      const res = await axios.put<FertilizerProduct>(
        `/api/v1/consumables/fertilizers/${id}`,
        body,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deleteFertilizer(id: string): Promise<void> {
    try {
      await axios.delete(`/api/v1/consumables/fertilizers/${id}`, {
        headers: authHeaders(),
      });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getAudit(filters: NutritionAuditFilters = {}): Promise<NutritionAudit> {
    try {
      const res = await axios.get<NutritionAudit>(`${base}/audit`, {
        params: filters,
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) { Sentry.captureException(err); throw err; }
  },

  async exportAudit(filters: NutritionAuditFilters = {}): Promise<Blob> {
    try {
      const res = await axios.get(`${base}/audit/export`, {
        params: filters,
        headers: authHeaders(),
        responseType: 'blob',
      });
      return res.data;
    } catch (err) { Sentry.captureException(err); throw err; }
  },

  /**
   * The tenant's active foliar programme, or null when none is active — the
   * route replies 204 with no body, which is a normal state, not an error.
   */
  async getActiveProgramme(): Promise<NutritionProgramme | null> {
    try {
      const res = await axios.get<NutritionProgramme>(`${base}/programmes/active`, {
        headers: authHeaders(),
      });
      return res.status === 204 ? null : res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getFoliarBoard(programmeId: string): Promise<FoliarBoard> {
    try {
      const res = await axios.get<FoliarBoard>(
        `${base}/programmes/${programmeId}/foliar-board`,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getRoundRoster(programmeId: string, roundId: string): Promise<FoliarRoster> {
    try {
      const res = await axios.get(`${base}/programmes/${programmeId}/rounds/${roundId}/roster`, { headers: authHeaders() });
      return res.data;
    } catch (err) { Sentry.captureException(err); throw err; }
  },

  async assignBulk(
    programmeId: string, roundId: string, sessionIds: string[], operatorId: string, machineId: string,
  ): Promise<AssignBulkResult> {
    try {
      const res = await axios.post(
        `${base}/programmes/${programmeId}/rounds/${roundId}/assign-bulk`,
        { sessionIds, operatorId, machineId },
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) { Sentry.captureException(err); throw err; }
  },

  // Converting a foliar round into sessions reuses `nutritionSessionService.convertRound`
  // (POST /programmes/:id/rounds/:roundId/convert) — do not add a second wrapper
  // for this route here. See FoliarBoardPage.onCreate.

  async getFoliarSessionDetail(sessionId: string): Promise<FoliarSessionDetail> {
    try {
      const res = await axios.get(`${base}/foliar-sessions/${sessionId}`, { headers: authHeaders() });
      return res.data;
    } catch (err) { Sentry.captureException(err); throw err; }
  },

  async listAwaitingFoliarSignoff(): Promise<FoliarAwaitingRow[]> {
    try {
      const res = await axios.get(`${base}/foliar-sessions/awaiting-signoff`, { headers: authHeaders() });
      return res.data;
    } catch (err) { Sentry.captureException(err); throw err; }
  },

  /**
   * Bulk sign-off. Ruling 2: the queue's PER-ROW action posts a one-item batch
   * here rather than calling POST /sessions/:id/signoff, because that route is
   * gated fertilizing:sessions while the sign-off tab is gated
   * fertilizing:signoff — a signoff-only manager would be refused on every row.
   * Do not "optimise" a single row onto the other endpoint.
   *
   * Per-item failures resolve at HTTP 200 as { results, okCount, failCount }.
   * failCount > 0 is a WARNING notice, never a thrown error.
   */
  async signOffFoliarBulk(items: Array<{ sessionId: string }>): Promise<FoliarSignOffBulkResult> {
    try {
      const res = await axios.post(`${base}/foliar-sessions/sign-off-bulk`, { items }, { headers: authHeaders() });
      return res.data;
    } catch (err) { Sentry.captureException(err); throw err; }
  },

};

// --- inventory types ---

export interface FertilizerStockRow {
  productId: string;
  productName: string;
  /**
   * Null when the product's stock ledger is in a unit-conflict state (Slice 4):
   * mixed-family transactions mean the backend can't safely sum a single number.
   * Never do arithmetic on this without a null check; render the
   * `inventory.unitConflict.badge` review pill instead of a number.
   */
  onHand: number | null;
  unit: string | null;
  /** Null alongside a conflicted onHand — the rand value can't be computed either. */
  value: number | null;
  /**
   * `status` includes 'unknown' for the same reason FertilizerLot.status does
   * (#541): Task 1 made dwellStatus return 'unknown' when days are null, and
   * getFertilizerStock copies that value straight through
   * (nutritionInventoryService.js:100,104 → :125). Reachable whenever a
   * surviving lot has a missing or invalid receivedAt. No behaviour change —
   * NutritionInventoryPage.tsx:48 early-returns on days == null before the
   * variant lookup — but the type must not claim a value the backend can send
   * is impossible.
   */
  dwell: { days: number | null; limit: number; status: 'ok' | 'low' | 'move' | 'unknown' } | null;
  /**
   * Why `dwell` is null, when it is (#541).
   * 'no_stock'   — on-hand is a MEASURED zero; there is nothing to dwell.
   * 'unmeasured' — on-hand is unknown, or no open lot describes the stock.
   * null         — dwell is present and legitimate.
   * A measured zero and an unknown are different facts; only one is "not measured".
   */
  dwellReason?: 'no_stock' | 'unmeasured' | null;
  /** 'conflict' when onHand is null due to a cross-family unit conflict. */
  stockStatus?: 'ok' | 'conflict';
  /** Present when stockStatus === 'conflict'; machine reason code (e.g. 'cross_family'). */
  conflictReason?: string;
}

export interface FertilizerStock {
  rows: FertilizerStockRow[];
  kpis: { onHandValue: number; dwellBreaches: number; dwellLow: number };
}

export interface FertilizerLot {
  _id: string;
  productId: string;
  productName?: string;
  qty: number;
  unit?: string;
  receivedAt: string;
  location?: string;
  dwellLimitDays: number;
  dwellDays: number | null;
  // 'unknown' added for #541: dwellStatus (Task 1) now returns 'unknown' when
  // dwell days are null, and getLots maps every returned lot through it — the
  // backend can send a value this type used to say was impossible.
  status: 'ok' | 'low' | 'move' | 'unknown';
}

export interface FertilizerMovement {
  _id: string;
  productId: string;
  productName: string;
  type: string;
  quantity: number;
  unit?: string;
  date: string;
  referenceId?: string;
}

export interface OrderToNeedLine {
  /**
   * Null when the line is an unresolvable soil product name
   * (`conflictReason: 'unknown_product'`) — the SoilProgram named a product the
   * Fertilizer catalogue does not hold, so there is no id to carry.
   */
  productId: string | null;
  productName?: string;
  unit: string;
  demand: number;
  /**
   * Null when the product is unit-conflicted (Slice 4, Decision B): the backend
   * has no trustworthy on-hand figure, so no arithmetic may run on this line.
   */
  onHand: number | null;
  /** Null alongside a conflicted onHand/status (unknown_product) — never sum or coerce to 0. */
  onOrder?: number | null;
  /** Null alongside a conflicted onHand/status — never sum or coerce to 0. */
  orderQty: number | null;
  orderBy: string | null;
  /**
   * 'conflict' when orderQty/onHand carry no trustworthy figure. The reason is
   * NOT always a unit problem: 'unknown_product' means the product is absent
   * from the catalogue entirely. Read `conflictReason` before wording anything.
   */
  status?: 'ok' | 'conflict';
  /**
   * Present when status === 'conflict'; machine reason code. Unit reasons
   * ('cross_family', 'pack_unit', 'invalid_stock_unit', 'missing_unit',
   * 'stock_unit_mismatch') and 'unknown_product' (not in the catalogue).
   */
  conflictReason?: string;
}

export interface OrderToNeedRoundLine {
  /** Null for an unresolvable soil product name — see OrderToNeedLine.productId. */
  productId: string | null;
  productName?: string;
  unit: string;
  demand: number;
  /** Null when the line is unit-conflicted (Slice 4, Decision B) — list figure-less, never sum. */
  orderQty: number | null;
  /**
   * 'conflict' when orderQty carries no trustworthy figure — a unit conflict OR
   * 'unknown_product' (the product is not in the catalogue at all).
   */
  status?: 'ok' | 'conflict';
  /**
   * Present when status === 'conflict'; machine reason code. Unit reasons
   * ('cross_family', 'pack_unit', 'invalid_stock_unit', 'missing_unit',
   * 'stock_unit_mismatch') and 'unknown_product' (not in the catalogue).
   */
  conflictReason?: string;
}

export interface OrderToNeedRound {
  roundId: string;
  name: string;
  month: number | null;
  plannedStart: string | null;
  orderBy: string | null;
  lines: OrderToNeedRoundLine[];
  /** Which demand stack this record came from. Optional for older server responses. */
  source?: 'foliar' | 'soil';
}

export interface OrderToNeedSeason {
  programmeId: string;
  season: string;
  rounds: OrderToNeedRound[];
}

export interface OrderToNeedSource {
  module: 'foliar' | 'soil';
  status: 'ok' | 'not_computed';
  /** Machine reason code, present only when status is 'not_computed'. */
  reason?: string;
}

export interface OrderToNeed {
  remaining: OrderToNeedLine[];
  orderBy: string | null;
  stockCover: number | null;
  roundCount: number;
  seasons?: OrderToNeedSeason[];
  /**
   * Per-stack coverage. An empty `remaining` means "everything is ordered" ONLY
   * when every source here is 'ok'; otherwise it means the demand was never
   * measured (#528). Optional so a cached response from an older server does
   * not break the page.
   */
  sources?: OrderToNeedSource[];
}

export interface FertilizerOrder {
  _id: string;
  status: 'draft' | 'ordered' | 'received';
  orderBy?: string;
  lines: { productId: string; productName?: string; qty: number; unit?: string }[];
  createdAt: string;
  receivedAt?: string;
}

// --- inventory functions ---

export const getInventoryStock = async (): Promise<FertilizerStock> =>
  (await axios.get(`${base}/inventory/stock`, { headers: authHeaders() })).data;

export const getLots = async (productId?: string): Promise<FertilizerLot[]> =>
  (await axios.get(`${base}/inventory/lots`, { headers: authHeaders(), params: { productId } })).data;

export const receiveStock = async (body: Record<string, unknown>): Promise<unknown> =>
  (await axios.post(`${base}/inventory/receive`, body, { headers: authHeaders() })).data;

export const closeLot = async (id: string): Promise<unknown> =>
  (await axios.post(`${base}/inventory/lots/${id}/close`, {}, { headers: authHeaders() })).data;

export const getInventoryMovements = async (
  params: { type?: string; productId?: string } = {}
): Promise<FertilizerMovement[]> =>
  (await axios.get(`${base}/inventory/movements`, { headers: authHeaders(), params })).data;

export const getOrderToNeed = async (): Promise<OrderToNeed> =>
  (await axios.get(`${base}/inventory/order-to-need`, { headers: authHeaders() })).data;

export const listOrders = async (): Promise<FertilizerOrder[]> =>
  (await axios.get(`${base}/inventory/orders`, { headers: authHeaders() })).data;

export const createOrder = async (body: {
  lines: FertilizerOrder['lines'];
  orderBy?: string | null;
}): Promise<FertilizerOrder> =>
  (await axios.post(`${base}/inventory/orders`, body, { headers: authHeaders() })).data;

export const updateOrder = async (
  id: string,
  status: FertilizerOrder['status']
): Promise<FertilizerOrder> =>
  (await axios.patch(`${base}/inventory/orders/${id}`, { status }, { headers: authHeaders() })).data;

export const deleteOrder = async (id: string): Promise<void> => {
  await axios.delete(`${base}/inventory/orders/${id}`, { headers: authHeaders() });
};

export const getNutritionSummary = async (): Promise<NutritionSummary> =>
  (await axios.get(`${base}/dashboard/summary`, { headers: authHeaders() })).data;

